import type { CloudReverbParams } from "./cloud-reverb-node";

/**
 * Shared definition of every tweakable reverb parameter — the metadata the
 * /drone playground and the fullscreen drone pad both need to render sliders.
 */

export type DroneParamGroup = "cloud" | "shimmer" | "shimmerPitch";

export interface DroneParamDef {
  key: keyof CloudReverbParams;
  label: string;
  min: number;
  max: number;
  step: number;
  defaultValue: number;
  unit?: string;
  group: DroneParamGroup;
}

export const DRONE_PARAM_DEFS: DroneParamDef[] = [
  // Cloud reverb
  { key: "cloudDecay", label: "Decay", min: 0.5, max: 30, step: 0.5, defaultValue: 8, unit: "s", group: "cloud" },
  { key: "cloudPreDelay", label: "Pre-Delay", min: 0, max: 0.2, step: 0.005, defaultValue: 0.05, unit: "s", group: "cloud" },
  { key: "cloudDamping", label: "Damping", min: 0, max: 1, step: 0.01, defaultValue: 0.3, group: "cloud" },
  { key: "cloudModDepth", label: "Mod Depth", min: 0, max: 1, step: 0.01, defaultValue: 0.5, group: "cloud" },
  { key: "cloudModRate", label: "Mod Rate", min: 0.1, max: 3, step: 0.05, defaultValue: 0.7, unit: "Hz", group: "cloud" },
  { key: "cloudDiffusion", label: "Diffusion", min: 0, max: 1, step: 0.01, defaultValue: 0.75, group: "cloud" },
  { key: "cloudMix", label: "Mix", min: 0, max: 1, step: 0.01, defaultValue: 0.5, group: "cloud" },

  // Shimmer reverb
  { key: "shimmerDecay", label: "Decay", min: 0.5, max: 30, step: 0.5, defaultValue: 6, unit: "s", group: "shimmer" },
  { key: "shimmerPreDelay", label: "Pre-Delay", min: 0, max: 0.2, step: 0.005, defaultValue: 0.01, unit: "s", group: "shimmer" },
  { key: "shimmerDamping", label: "Damping", min: 0, max: 1, step: 0.01, defaultValue: 0.25, group: "shimmer" },
  { key: "shimmerModDepth", label: "Mod Depth", min: 0, max: 1, step: 0.01, defaultValue: 0.4, group: "shimmer" },
  { key: "shimmerModRate", label: "Mod Rate", min: 0.1, max: 3, step: 0.05, defaultValue: 0.5, unit: "Hz", group: "shimmer" },
  { key: "shimmerDiffusion", label: "Diffusion", min: 0, max: 1, step: 0.01, defaultValue: 0.7, group: "shimmer" },
  { key: "shimmerMix", label: "Mix", min: 0, max: 1, step: 0.01, defaultValue: 0.5, group: "shimmer" },

  // Shimmer pitch
  { key: "shimmerAmount", label: "Amount", min: 0, max: 0.8, step: 0.01, defaultValue: 0.4, group: "shimmerPitch" },
  { key: "shimmerPitch1", label: "Voice 1", min: -24, max: 24, step: 1, defaultValue: 12, unit: "semi", group: "shimmerPitch" },
  // Shimmer voices default to octaves only. A non-octave voice (e.g. +7) in
  // the feedback loop walks the circle of fifths — 5th, 9th, 6th, 3rd, maj 7th —
  // and puts non-chord tones under a root+fifth drone (measured at -21 dB).
  { key: "shimmerPitch2", label: "Voice 2", min: -24, max: 24, step: 1, defaultValue: 24, unit: "semi", group: "shimmerPitch" },
  { key: "shimmerBalance", label: "Balance", min: 0, max: 1, step: 0.01, defaultValue: 0.3, group: "shimmerPitch" },
];

export const DRONE_PARAM_DEFS_BY_KEY = new Map(
  DRONE_PARAM_DEFS.map((def) => [def.key, def]),
);

export function droneParamsForGroup(group: DroneParamGroup): DroneParamDef[] {
  return DRONE_PARAM_DEFS.filter((def) => def.group === group);
}

export function formatDroneParamValue(val: number, def: DroneParamDef): string {
  if (def.step >= 1) return `${val}${def.unit ?? ""}`;
  if (def.step >= 0.1) return `${val.toFixed(1)}${def.unit ?? ""}`;
  return `${val.toFixed(2)}${def.unit ?? ""}`;
}

/** Pad-level controls that live outside CloudReverbParams. */
export const PAD_VOLUME_RANGE = { min: -48, max: 0, step: 1 };
export const PAD_TRANSITION_RANGE = { min: 0.5, max: 15, step: 0.5 };
export const PAD_LP_RANGE = { min: 100, max: 8000, step: 50 };
export const PAD_HP_RANGE = { min: 20, max: 2000, step: 10 };

/**
 * Reference transition length, in seconds: the crossfade a preset change gets
 * and the point where every preset's note envelope plays exactly as authored.
 * Moving the transition control scales both away from here together.
 */
export const DEFAULT_TRANSITION_SECONDS = 3;
